import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import keycode from 'keycode';
import { IconCheckSolid, IconArrowOpenEndSolid } from '@instructure/ui-icons';
import { themeable } from '@instructure/ui-themeable';
import { uid } from '@instructure/uid';
import { controllable } from '@instructure/ui-prop-types';
import { omitProps, getElementType } from '@instructure/ui-react-utils';
import { createChainedFunction } from '@instructure/ui-utils';
import { isActiveElement, findDOMNode } from '@instructure/ui-dom-utils';
import { testable } from '@instructure/ui-testable';
import { MenuContext } from "../../MenuContext.js";
var styles = {
  componentId: 'sJGfW',
  template: function template(theme) {
    return "\n\n.sJGfW_bGBk{-ms-user-select:none;-webkit-user-select:none;background:".concat(theme.background || 'inherit', ";border:none;border-radius:0;border-radius:initial;box-sizing:border-box;cursor:pointer;display:block;font-family:").concat(theme.fontFamily || 'inherit', ";font-size:").concat(theme.fontSize || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', ";margin:0;outline:none;padding:").concat(theme.padding || 'inherit', ";position:relative;text-align:start;transition:background 0.2s;user-select:none;width:100%}\n\n[dir=ltr] .sJGfW_bGBk{text-align:left}\n\n[dir=rtl] .sJGfW_bGBk{text-align:right}\n\n.sJGfW_bGBk.sJGfW_eyNH .sJGfW_dnnz{inset-inline-end:").concat(theme.iconPadding || 'inherit', ";inset-inline-start:auto}\n\n[dir=ltr] .sJGfW_bGBk.sJGfW_eyNH .sJGfW_dnnz{left:auto;right:").concat(theme.iconPadding || 'inherit', "}\n\n[dir=rtl] .sJGfW_bGBk.sJGfW_eyNH .sJGfW_dnnz{left:").concat(theme.iconPadding || 'inherit', ";right:auto}\n\n.sJGfW_bGBk[role=menuitemcheckbox],.sJGfW_bGBk[role=menuitemradio]{-webkit-padding-start:").concat(theme.labelPadding || 'inherit', ";padding-inline-start:").concat(theme.labelPadding || 'inherit', "}\n\n[dir=ltr] .sJGfW_bGBk[role=menuitemcheckbox],[dir=ltr] .sJGfW_bGBk[role=menuitemradio]{padding-left:").concat(theme.labelPadding || 'inherit', "}\n\n[dir=rtl] .sJGfW_bGBk[role=menuitemcheckbox],[dir=rtl] .sJGfW_bGBk[role=menuitemradio]{padding-right:").concat(theme.labelPadding || 'inherit', "}\n\n.sJGfW_bGBk[role=menuitemcheckbox] .sJGfW_dnnz,.sJGfW_bGBk[role=menuitemradio] .sJGfW_dnnz{inset-inline-end:auto;inset-inline-start:").concat(theme.iconPadding || 'inherit', "}\n\n[dir=ltr] .sJGfW_bGBk[role=menuitemcheckbox] .sJGfW_dnnz,[dir=ltr] .sJGfW_bGBk[role=menuitemradio] .sJGfW_dnnz{left:").concat(theme.iconPadding || 'inherit', ";right:auto}\n\n[dir=rtl] .sJGfW_bGBk[role=menuitemcheckbox] .sJGfW_dnnz,[dir=rtl] .sJGfW_bGBk[role=menuitemradio] .sJGfW_dnnz{left:auto;right:").concat(theme.iconPadding || 'inherit', "}\n\n.sJGfW_bGBk.sJGfW_cSen,.sJGfW_bGBk:active,.sJGfW_bGBk:focus,.sJGfW_bGBk:hover,.sJGfW_bGBk[aria-expanded=true]{background:").concat(theme.activeBackground || 'inherit', "}\n\n.sJGfW_bGBk.sJGfW_cSen .sJGfW_blJt,.sJGfW_bGBk:active .sJGfW_blJt,.sJGfW_bGBk:focus .sJGfW_blJt,.sJGfW_bGBk:hover .sJGfW_blJt,.sJGfW_bGBk[aria-expanded=true] .sJGfW_blJt{color:").concat(theme.activeLabelColor || 'inherit', "}\n\n.sJGfW_bGBk.sJGfW_cSen .sJGfW_dnnz,.sJGfW_bGBk:active .sJGfW_dnnz,.sJGfW_bGBk:focus .sJGfW_dnnz,.sJGfW_bGBk:hover .sJGfW_dnnz,.sJGfW_bGBk[aria-expanded=true] .sJGfW_dnnz{color:").concat(theme.activeIconColor || 'inherit', "}\n\n.sJGfW_bGBk::-moz-focus-inner{border:0;margin:0;padding:0}\n\n.sJGfW_bGBk[aria-disabled]{cursor:not-allowed;opacity:0.5;pointer-events:none}\n\n.sJGfW_blJt{color:").concat(theme.labelColor || 'inherit', "}\n\n.sJGfW_dnnz{align-items:center;color:").concat(theme.iconColor || 'inherit', ";display:flex;height:100%;position:absolute;top:0;width:1em}\n\na.sJGfW_bGBk,a.sJGfW_bGBk:active,a.sJGfW_bGBk:focus,a.sJGfW_bGBk:hover,a.sJGfW_bGBk:link,a.sJGfW_bGBk:visited{text-decoration:none}");
  },
  'root': 'sJGfW_bGBk',
  'flyout': 'sJGfW_eyNH',
  'icon': 'sJGfW_dnnz',
  'active': 'sJGfW_cSen',
  'label': 'sJGfW_blJt'
};
import theme from "./theme.js";
/**
---
parent: Menu
id: Menu.Item
---
**/

var _ref = /*#__PURE__*/React.createElement(IconCheckSolid, null);

var _ref2 = /*#__PURE__*/React.createElement(IconArrowOpenEndSolid, null);

var MenuItem = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(MenuItem, _Component);

  var _super = _createSuper(MenuItem);

  function MenuItem(props) {
    var _this;

    _classCallCheck(this, MenuItem);

    _this = _super.call(this);

    _this.handleClick = function (e) {
      var _this$props = _this.props,
          onSelect = _this$props.onSelect,
          onClick = _this$props.onClick,
          disabled = _this$props.disabled,
          value = _this$props.value;
      var selected = !_this.selected;

      if (disabled) {
        e.preventDefault();
        return;
      }

      if (typeof _this.props.selected === 'undefined') {
        _this.setState({
          selected: selected
        });
      }

      if (typeof onSelect === 'function') {
        e.persist();
        onSelect(e, value, selected, _assertThisInitialized(_this));
      }

      if (typeof onClick === 'function') {
        onClick(e);
      }
    };

    _this.handleKeyDown = function (e) {
      var spaceKey = e.keyCode === keycode.codes.space;
      var enterKey = e.keyCode === keycode.codes.enter;

      if (spaceKey || enterKey) {
        e.preventDefault();
        e.stopPropagation();

        if (enterKey) {
          // handle space key on keyUp for FF
          findDOMNode(_this._node).click(); // eslint-disable-line react/no-find-dom-node
        }
      }
    };

    _this.handleKeyUp = function (e) {
      var spaceKey = e.keyCode === keycode.codes.space;
      var enterKey = e.keyCode === keycode.codes.enter;

      if (spaceKey || enterKey) {
        e.preventDefault();
        e.stopPropagation();

        if (spaceKey) {
          findDOMNode(_this._node).click(); // eslint-disable-line react/no-find-dom-node
        }
      }
    };

    _this.handleMouseOver = function (event) {
      _this.focus();

      if (typeof _this.props.onMouseOver === 'function') {
        _this.props.onMouseOver(event, _assertThisInitialized(_this));
      }
    };

    if (typeof props.selected === 'undefined') {
      _this.state = {
        selected: props.defaultSelected
      };
    }

    _this.labelId = uid('MenuItem__label');
    return _this;
  }

  _createClass(MenuItem, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var context = MenuContext.getMenuContext(this.context);

      if (context && context.registerMenuItem) {
        context.registerMenuItem(this);
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      var context = MenuContext.getMenuContext(this.context);

      if (context && context.registerMenuItem) {
        context.removeMenuItem(this);
      }
    }
  }, {
    key: "focus",
    value: function focus() {
      findDOMNode(this._node).focus(); // eslint-disable-line react/no-find-dom-node
    }
  }, {
    key: "renderContent",
    value: function renderContent() {
      var _this$props2 = this.props,
          children = _this$props2.children,
          type = _this$props2.type;
      return /*#__PURE__*/React.createElement("span", null, (type === 'checkbox' || type === 'radio') && /*#__PURE__*/React.createElement("span", {
        className: styles.icon
      }, this.selected && _ref), /*#__PURE__*/React.createElement("span", {
        className: styles.label,
        id: this.labelId
      }, children), type === 'flyout' && /*#__PURE__*/React.createElement("span", {
        className: styles.icon
      }, _ref2));
    }
  }, {
    key: "render",
    value: function render() {
      var _classes,
          _this2 = this;

      var _this$props3 = this.props,
          disabled = _this$props3.disabled,
          controls = _this$props3.controls,
          onKeyDown = _this$props3.onKeyDown,
          onKeyUp = _this$props3.onKeyUp,
          type = _this$props3.type,
          href = _this$props3.href;
      var props = omitProps(this.props, MenuItem.propTypes);
      var ElementType = this.elementType;
      var classes = (_classes = {}, _defineProperty(_classes, styles.root, true), _defineProperty(_classes, styles.flyout, type === 'flyout'), _classes);
      return /*#__PURE__*/React.createElement(ElementType // eslint-disable-line jsx-a11y/mouse-events-have-key-events
      , Object.assign({
        tabIndex: "-1" // note: tabIndex can be overridden by Menu or MenuItemGroup components

      }, props, {
        href: href,
        role: this.role,
        "aria-labelledby": this.labelId,
        "aria-disabled": disabled ? 'true' : null,
        "aria-controls": controls,
        "aria-checked": type === 'checkbox' || type === 'radio' ? this.selected ? 'true' : 'false' : null,
        onClick: this.handleClick,
        onKeyUp: createChainedFunction(onKeyUp, this.handleKeyUp),
        onKeyDown: createChainedFunction(onKeyDown, this.handleKeyDown),
        ref: function ref(c) {
          _this2._node = c;
        },
        className: classnames(classes),
        onMouseOver: this.handleMouseOver
      }), this.renderContent());
    }
  }, {
    key: "elementType",
    get: function get() {
      return getElementType(MenuItem, this.props);
    }
  }, {
    key: "role",
    get: function get() {
      switch (this.props.type) {
        case 'checkbox':
          return 'menuitemcheckbox';

        case 'radio':
          return 'menuitemradio';

        case 'flyout':
          return 'button';

        default:
          return 'menuitem';
      }
    }
  }, {
    key: "selected",
    get: function get() {
      return typeof this.props.selected === 'undefined' ? this.state.selected : this.props.selected;
    }
  }, {
    key: "focused",
    get: function get() {
      return isActiveElement(this._node);
    }
  }]);

  MenuItem.displayName = "MenuItem";
  return MenuItem;
}(Component), _class2.propTypes = {
  /* the menu item label */
  children: PropTypes.node.isRequired,

  /* whether to set the menu item state to selected or not on initial render */
  defaultSelected: PropTypes.bool,

  /**
   * whether the menu item is selected or not (must be accompanied by an `onSelect` prop)
   */
  selected: controllable(PropTypes.bool, 'onSelect', 'defaultSelected'),

  /**
   * when used with the `selected` prop, the component will not control its own state
   */
  onSelect: PropTypes.func,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  onMouseOver: PropTypes.func,

  /**
   * the id of the element that the menu item will act upon
   */
  controls: PropTypes.string,
  disabled: PropTypes.bool,

  /**
   * the element type to render as (will default to `<a>` if href is provided)
   */
  as: PropTypes.elementType,
  // eslint-disable-line react/require-default-props
  type: PropTypes.oneOf(['button', 'checkbox', 'radio', 'flyout']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  href: PropTypes.string
}, _class2.defaultProps = {
  type: 'button',
  disabled: false,
  onSelect: function onSelect(e, value, selected, item) {},
  defaultSelected: void 0,
  selected: void 0,
  onClick: void 0,
  onKeyDown: void 0,
  onKeyUp: void 0,
  onMouseOver: void 0,
  controls: void 0,
  value: void 0,
  href: void 0
}, _class2.contextTypes = MenuContext.types, _temp)) || _class) || _class);
export default MenuItem;
export { MenuItem };